import type { MasterData, Item } from '~/types/master-data';
import type { SearchConditions } from '~/types/search-conditions';

interface GenerateStaticPathProps {
  searchConditions: SearchConditions;
  masterData: MasterData;
}

export function generateStaticPath({
  searchConditions,
  masterData,
}: GenerateStaticPathProps) {
  for (const [key, values] of Object.entries(searchConditions)) {
    if (Array.isArray(values) && values.length > 1) {
      return null;
    } else if (key === 'freeWord' && values.length > 0) {
      return null;
    } else if (key === 'annualSalaryMax' && values > 0) {
      return null;
    }
  }
  // 都道府県市区町村以外の条件と最低年収がある場合は静的なパスは生成しない
  if (
    (searchConditions.licenseNames.length > 0 ||
      searchConditions.categoryNames.length > 0 ||
      searchConditions.typeNames.length > 0 ||
      searchConditions.contractNames.length > 0 ||
      searchConditions.featureNames.length > 0) &&
    searchConditions.annualSalaryMin &&
    searchConditions.annualSalaryMin > 0
  ) {
    return null;
  }

  // 職種, 工事の種類, 資格のうち2つ以上が設定されている場合は静的なパスは生成しない
  if (
    [
      searchConditions.licenseNames.length,
      searchConditions.categoryNames.length,
      searchConditions.typeNames.length,
    ].filter((count) => count > 0).length >= 2
  ) {
    return null;
  }

  let staticPath = buildBasePath(masterData, searchConditions);

  if (staticPath) {
    staticPath = appendAdditionalPaths(
      masterData,
      staticPath,
      searchConditions,
    );
  }

  return staticPath || null;
}

const findIdByName = (name: string, items: Item[]) => {
  const item = items.find((item) => item.name === name);
  return item ? item.id : null;
};

const buildBasePath = (
  masterData: MasterData,
  conditions: SearchConditions,
) => {
  if (conditions.licenseNames.length === 1 && masterData.licenses) {
    const id = findIdByName(conditions.licenseNames[0], masterData.licenses);
    return id ? `/licenses/license_${id}` : null;
  } else if (conditions.categoryNames.length === 1 && masterData.categories) {
    const id = findIdByName(conditions.categoryNames[0], masterData.categories);
    return id ? `/categories/category_${id}` : null;
  } else if (conditions.typeNames.length === 1 && masterData.types) {
    const id = findIdByName(conditions.typeNames[0], masterData.types);
    return id ? `/types/type_${id}` : null;
  } else if (conditions.contractNames.length === 1 && masterData.contracts) {
    const id = findIdByName(conditions.contractNames[0], masterData.contracts);
    return id ? `/contracts/contract_${id}` : null;
  } else if (conditions.featureNames.length === 1 && masterData.features) {
    const id = findIdByName(conditions.featureNames[0], masterData.features);
    return id ? `/features/feature_${id}` : null;
  } else if (conditions.annualSalaryMin && conditions.annualSalaryMin > 0) {
    return `/annual_salary_mins/${conditions.annualSalaryMin}`;
  } else if (
    conditions.prefectureNames.length === 1 &&
    masterData.prefectures
  ) {
    const id = findIdByName(
      conditions.prefectureNames[0],
      masterData.prefectures,
    );
    return id ? `/prefectures/prefecture_${id}` : null;
  }

  return '/jobs';
};

interface AdditionalKey {
  key: keyof SearchConditions;
  baseKey: string;
  dataKey: keyof MasterData;
}

const appendAdditionalPaths = (
  masterData: MasterData,
  basePath: string,
  conditions: SearchConditions,
) => {
  let additionalKeys: AdditionalKey[] = [
    {
      key: 'prefectureNames',
      baseKey: '/prefectures/',
      dataKey: 'prefectures',
    },
    { key: 'cityNames', baseKey: '/cities/', dataKey: 'cities' },
    { key: 'contractNames', baseKey: '/contracts/', dataKey: 'contracts' },
    { key: 'featureNames', baseKey: '/features/', dataKey: 'features' },
  ];

  // ベースキーが`annual_salary_mins`の場合は、additionalKeysOnlyPrefectureCityを使用
  if (basePath.includes('/annual_salary_mins/')) {
    // contractNames, featureNamesを削除
    additionalKeys = additionalKeys.filter(
      (key) => key.key !== 'contractNames' && key.key !== 'featureNames',
    );
  }

  // 都道府県が設定されていない場合は、市区町村は設定できないようにする
  if (conditions.prefectureNames.length === 0) {
    additionalKeys = additionalKeys.filter((key) => key.key !== 'cityNames');
  } else if (conditions.cityNames.length > 0) {
    // 都道府県が設定され、市区町村を含む場合は都道府県がその市区町村に含まれるかどうかを確認
    const city = masterData.cities?.find(
      (city) => city.name === conditions.cityNames[0],
    );
    const prefecture = masterData.prefectures?.find(
      (prefecture) => prefecture.name === conditions.prefectureNames[0],
    );
    // 一致しない場合は、市区町村は設定できないようにする
    if (city?.prefecture_id !== prefecture?.id) {
      additionalKeys = additionalKeys.filter((key) => key.key !== 'cityNames');
    }
  }

  for (const { key, baseKey, dataKey } of additionalKeys) {
    const conditionValues = conditions[key]; // 一時変数に格納
    if (
      Array.isArray(conditionValues) &&
      conditionValues.length === 1 &&
      !basePath.includes(baseKey)
    ) {
      const id = findIdByName(
        conditionValues[0],
        masterData[dataKey] as Item[],
      );
      if (id) {
        const modelKey = key.replace(/Names$/, '');
        basePath += `/${modelKey}_${id}`;
      }
    }
  }

  return basePath;
};
